import axios, {AxiosInstance, AxiosResponse} from 'axios';
import {
    LoginRequest,
    AuthResponse,
    User,
    CreateUserRequest,
    AgentDTO,
    AgentInfo,
    ResponsePageAgentDTO,
    UpdateAgentDTO,
    AddAgentRequest,
    ExportAgentsParams,
    ExportAgentsResponse,
    ResponsePageFajrParticipant,
    FajrParticipant,
    UpdateFajrParticipantRequest
} from '../types/auth';
import {
    MerchantSummaryDTO,
    MerchantEventDTO,
    ExportMerchantEventsParams
} from '../types/auth';
import {
    ResponsePageStorageFileDTO,
    ActionResponseStorageFileDTO,
    StorageFileInfo,
    StorageLink,
    AddLink
} from '../types/auth';
import {
    ActionResponseResponsePageNewsletterWebDTO,
    CreateNewsletterRequest,
    TestNewsletterRequest,
    LaunchNewsletterRequest,
    TestExistingNewsletterRequest,
    CreateWaNewsletterRequest,
    SenderDTO,
    AddSenderRequest,
    UpdateSenderRequest
} from '../types/auth';
import {
    ResponsePageWAChatDTO,
    WAMessageDTO,
    SendWAMessageRequest
} from '../types/auth';
import {
    WANoteDTO,
    UpdateWANoteRequest
} from '../types/auth';
import {
    ResponsePageMbcAgentData,
    ProjectWebDTO,
    UnitWebDTO,
    OfferMerchant,
    MerchantDetail,
    AddMerchantRequest,
    AddSlotsRequest,
    ResponsePageWebTemplateDTO,
    CreateTemplateRequest
} from '../types/auth';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://office.backend.ae';

class ApiService {
    private api: AxiosInstance;
    private redirectingToLogin = false;

    constructor() {
        this.api = axios.create({
            baseURL: BASE_URL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        this.api.interceptors.request.use((config) => {
            console.log('🚀 API Request:', {
                url: config.url,
                method: config.method,
                data: config.data,
                headers: config.headers
            });

            const basicAuth = localStorage.getItem('basicAuth');
            if (basicAuth && basicAuth.startsWith('Basic ')) {
                config.headers.Authorization = basicAuth;
            } else if (config.headers?.Authorization) {
                config.headers.Authorization = undefined;
            }
            return config;
        });

        this.api.interceptors.response.use(
            (response) => {
                console.log('✅ API Response:', {
                    url: response.config.url,
                    method: response.config.method,
                    status: response.status,
                    statusText: response.statusText,
                    data: response.data,
                    headers: response.headers
                });
                return response;
            },
            async (error) => {
                console.log('❌ API Error:', {
                    url: error.config?.url,
                    method: error.config?.method,
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    headers: error.response?.headers,
                    message: error.message
                });

                // Обрабатываем 401 и 403 как ошибки аутентификации
                if (error.response?.status === 401 || error.response?.status === 403) {
                    // Проверяем, не является ли это бизнес-ошибкой (например, success=false)
                    const responseData = error.response?.data;
                    if (responseData && typeof responseData === 'object' && 'success' in responseData && 'msg' in responseData) {
                        console.log('✋ Business logic error detected, skipping auth cleanup');
                        return Promise.reject(error);
                    }

                    this.clearTokens();
                    this.redirectToLogin();
                }
                return Promise.reject(error);
            }
        );
    }

    private clearTokens(): void {
        localStorage.removeItem('basicAuth');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('adminSelectedTab');
    }

    private redirectToLogin(): void {
        // Предотвращаем множественные редиректы
        if (this.redirectingToLogin) {
            return;
        }
        this.redirectingToLogin = true;
        
        // Небольшая задержка, чтобы дать время обработать очередь и показать ошибку
        setTimeout(() => {
            const redirect = `${window.location.pathname}${window.location.search}`;
            const loginPath = redirect && redirect !== '/' && !redirect.startsWith('/login')
                ? `/login?redirect=${encodeURIComponent(redirect)}`
                : '/login';
            window.location.href = loginPath;
        }, 100);
    }

    private logout(): void {
        this.clearTokens();
        this.redirectToLogin();
    }

    async login(credentials: LoginRequest): Promise<AuthResponse> {
        const response: AxiosResponse<AuthResponse> = await this.api.post(
            '/api/v1/auth/login',
            credentials
        );
        return response.data;
    }

    async logoutUser(): Promise<void> {
        this.logout();
    }

    async getUsers(): Promise<User[]> {
        const response = await this.api.get('/api/v1/users');
        return response.data.users || response.data;
    }

    async createUser(userData: CreateUserRequest): Promise<User> {
        const response: AxiosResponse<User> = await this.api.post(
            '/api/v1/users',
            userData
        );
        return response.data;
    }

    async deleteUser(userId: number): Promise<void> {
        await this.api.delete('/api/v1/users', {
            params: {id: userId}
        });
    }

    async updateUser(request: { id: number; authorities: string[] }): Promise<User> {
        const response: AxiosResponse<User> = await this.api.post(
            '/api/v1/users/update',
            request
        );
        return response.data;
    }

    async getMerchantConfig(merchantName: string): Promise<{ data?: { daysUntilNextEvent?: number } }> {
        const response: AxiosResponse<{ data?: { daysUntilNextEvent?: number } }> = await this.api.get(
            '/api/v1/users/merchant_config',
            { params: { merchantName } }
        );
        return response.data;
    }

    async updateMerchant(merchantData: { name: string; config: { daysUntilNextEvent: number } }): Promise<any> {
        const response: AxiosResponse<any> = await this.api.post(
            '/api/v1/users/update_merchant',
            merchantData
        );
        return response.data;
    }

    async getUserAuthorities(): Promise<string[]> {
        const response: AxiosResponse<string[]> = await this.api.get('/api/v1/users/authorities');
        return response.data;
    }

    async getAgents(page?: number, size?: number, q?: string, email_state?: string, printed?: boolean, state?: string): Promise<ResponsePageAgentDTO> {
        const params: any = {};
        if (page !== undefined) params.page = page;
        if (size !== undefined) params.size = size;
        if (q) params.q = q;
        if (email_state && email_state !== 'all') params.email_state = email_state;
        if (printed !== undefined) params.printed = printed;
        if (state && state !== 'all') params.state = state;

        const response: AxiosResponse<ResponsePageAgentDTO> = await this.api.get('/api/v1/agents/list', {params});
        return response.data;
    }

    async getMBCAgents(page?: number, size?: number, q?: string, onlyWithLeads?: boolean): Promise<ResponsePageMbcAgentData> {
        const params: any = {};
        if (page !== undefined) params.page = page;
        if (size !== undefined) params.size = size;
        if (q) params.q = q;
        if (onlyWithLeads !== undefined) params.onlyWithLeads = onlyWithLeads;

        const response: AxiosResponse<ResponsePageMbcAgentData> = await this.api.get('/api/v1/mbc', {params});
        return response.data;
    }

    async getFajrParticipants(page?: number, size?: number, q?: string, approved?: boolean): Promise<ResponsePageFajrParticipant> {
        const params: any = {};
        if (page !== undefined) params.page = page;
        if (size !== undefined) params.size = size;
        if (q) params.q = q;
        if (approved !== undefined) params.approved = approved;

        const response: AxiosResponse<ResponsePageFajrParticipant> = await this.api.get('/api/v1/fajr_city/list', {params});
        return response.data;
    }

    async getFajrParticipant(participantId: string): Promise<FajrParticipant> {
        const response: AxiosResponse<FajrParticipant> = await this.api.get(`/api/v1/fajr_city/${participantId}`);
        return response.data;
    }

    async updateFajrParticipant(participantId: string, request: UpdateFajrParticipantRequest): Promise<void> {
        await this.api.post(`/api/v1/fajr_city/${participantId}`, request);
    }

    async refreshMBC(): Promise<void> {
        await this.api.get('/api/v1/mbc/refresh');
    }

    async getAgentInfo(agentId: string): Promise<AgentInfo> {
        const response: AxiosResponse<AgentInfo> = await this.api.get(`/api/v1/agents/${agentId}`);
        return response.data;
    }

    async scanQr(agentId: string, event: 'visited'): Promise<void> {
        await this.api.post('/api/v1/merchants/qr/scan', {
            agentId,
            event
        });
    }

    async merchantScanQr(agentId: string): Promise<AgentInfo> {
        const response: AxiosResponse<AgentInfo> = await this.api.post('/api/v1/merchants/qr/scan', {
            agentId
        });
        return response.data;
    }

    async resendMail(agentId: string): Promise<void> {
        await this.api.get(`/api/v1/agents/${agentId}/resend_mail`);
    }

    async updateAgent(agentId: string, updateData: UpdateAgentDTO): Promise<{
        success: boolean,
        msg: string,
        body: AgentDTO
    }> {
        const response: AxiosResponse<{ success: boolean, msg: string, body: AgentDTO }> = await this.api.post(
            `/api/v1/agents/${agentId}/update`,
            updateData
        );
        return response.data;
    }

    async updateAgentPrinted(agentId: string, printed: boolean): Promise<{
        success: boolean,
        msg: string,
        body: AgentDTO
    }> {
        const response: AxiosResponse<{ success: boolean, msg: string, body: AgentDTO }> = await this.api.get(
            `/api/v1/agents/${agentId}/printed`,
            {params: {state: printed}}
        );
        return response.data;
    }

    async addAgentEvent(agentId: string, eventData: { guests: Array<{ name: string; phone: string }> }): Promise<{
        success: boolean
    }> {
        const response: AxiosResponse<{ success: boolean }> = await this.api.post(
            `/api/v1/agents/${agentId}/new_event`,
            eventData
        );
        return response.data;
    }

    async deleteAgent(agentId: string, reason: string): Promise<{ success: boolean, msg: string }> {
        const response: AxiosResponse<{ success: boolean, msg: string }> = await this.api.delete(
            `/api/v1/agents/${agentId}`,
            {data: {reason}}
        );
        return response.data;
    }

    async blockAgent(agentId: string, blocked: boolean): Promise<{ success: boolean, msg: string }> {
        const response: AxiosResponse<{ success: boolean, msg: string }> = await this.api.post(
            `/api/v1/agents/${agentId}`,
            {blocked}
        );
        return response.data;
    }

    async exportAgents(exportParams: ExportAgentsParams): Promise<ExportAgentsResponse> {
        const {
            periodMode,
            startDate,
            endDate,
            q,
            emailState,
            printed,
            state
        } = exportParams;
        const queryParams: any = {};
        queryParams.period_mode = periodMode;
        if (periodMode === 'range') {
            if (startDate) queryParams.start_date = startDate;
            if (endDate) queryParams.end_date = endDate;
        }
        if (q) queryParams.q = q;
        if (emailState) queryParams.email_state = emailState;
        if (printed !== undefined) queryParams.printed = printed;
        if (state) queryParams.state = state;

        const response = await this.api.get('/api/v1/agents/export', {
            params: queryParams,
            responseType: 'blob',
            validateStatus: (status) => status >= 200 && status < 300
        });

        const rawStatus = response.headers['x-export-status'];
        if (rawStatus === 'empty') {
            return {
                success: true,
                msg: 'No agents found for selected filters',
                status: 'empty',
                appliedPeriodMode: periodMode
            };
        }

        // Получаем имя файла из заголовка Content-Disposition
        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'agents_export';

        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                fileName = filenameMatch[1];
            }
        }

        return {
            success: true,
            msg: fileName,
            body: response.data,
            status: 'completed',
            appliedPeriodMode: periodMode
        };
    }

    async getOfferMerchants(q?: string): Promise<MerchantSummaryDTO[]> {
        const params: any = {};
        if (q && q.trim()) {
            params.q = q.trim();
        }
        const response: AxiosResponse<MerchantSummaryDTO[]> = await this.api.get('/api/v1/agents/merchants', { params });
        return response.data;
    }

    async getOfferMerchantEvents(
        merchantId: number,
        params: {
            periodMode: 'range' | 'all_time';
            startDate?: string;
            endDate?: string;
            q?: string;
            emailState?: 'sent' | 'not_sent';
            printed?: boolean;
            state?: 'active' | 'blocked' | 'deleted';
        }
    ): Promise<MerchantEventDTO[]> {
        const queryParams: any = {
            period_mode: params.periodMode
        };
        if (params.periodMode === 'range') {
            if (params.startDate) queryParams.start_date = params.startDate;
            if (params.endDate) queryParams.end_date = params.endDate;
        }
        if (params.q) queryParams.q = params.q;
        if (params.emailState) queryParams.email_state = params.emailState;
        if (params.printed !== undefined) queryParams.printed = params.printed;
        if (params.state) queryParams.state = params.state;

        const response: AxiosResponse<MerchantEventDTO[]> = await this.api.get(
            `/api/v1/agents/merchants/${merchantId}/events`,
            { params: queryParams }
        );
        return response.data;
    }

    async exportMerchantEvents(exportParams: ExportMerchantEventsParams): Promise<ExportAgentsResponse> {
        const {
            periodMode,
            startDate,
            endDate,
            q,
            emailState,
            printed,
            state
        } = exportParams;
        const queryParams: any = {
            period_mode: periodMode
        };
        if (periodMode === 'range') {
            if (startDate) queryParams.start_date = startDate;
            if (endDate) queryParams.end_date = endDate;
        }
        if (q) queryParams.q = q;
        if (emailState) queryParams.email_state = emailState;
        if (printed !== undefined) queryParams.printed = printed;
        if (state) queryParams.state = state;

        const response = await this.api.get('/api/v1/agents/merchants/export', {
            params: queryParams,
            responseType: 'blob',
            validateStatus: (status) => status >= 200 && status < 300
        });

        const rawStatus = response.headers['x-export-status'];
        if (rawStatus === 'empty') {
            return {
                success: true,
                msg: 'No merchant events found for selected filters',
                status: 'empty',
                appliedPeriodMode: periodMode
            };
        }

        const contentDisposition = response.headers['content-disposition'];
        let fileName = 'merchant_events_export.zip';
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
                fileName = filenameMatch[1];
            }
        }

        return {
            success: true,
            msg: fileName,
            body: response.data,
            status: 'completed',
            appliedPeriodMode: periodMode
        };
    }

    async addAgent(request: AddAgentRequest): Promise<any> {
        // request now sends imageLink instead of imageBase64
        const response: AxiosResponse<any> = await this.api.post(
            `/api/v1/agents`,
            request
        );
        return response.data;
    }

    // Storage file methods
    async getFiles(page?: number, size?: number, q?: string): Promise<ResponsePageStorageFileDTO> {
        const params: any = {};
        if (page !== undefined) params.page = page;
        if (size !== undefined) params.size = size;
        if (q) params.q = q;

        const response: AxiosResponse<ResponsePageStorageFileDTO> = await this.api.get('/api/v1/storage', {params});
        return response.data;
    }

    async uploadFile(file: File, name: string, description: string): Promise<ActionResponseStorageFileDTO> {
        const formData = new FormData();
        formData.append('file', file);

        const response: AxiosResponse<ActionResponseStorageFileDTO> = await this.api.post(
            `/api/v1/storage?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description)}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    }

    async deleteFile(fileId: string): Promise<{ success: boolean }> {
        const response: AxiosResponse<{ success: boolean }> = await this.api.delete(`/api/v1/storage/${fileId}`);
        return response.data;
    }

    async getFileDetails(fileId: string): Promise<StorageFileInfo> {
        const response: AxiosResponse<StorageFileInfo> = await this.api.get(`/api/v1/storage/${fileId}`);
        return response.data;
    }

    async addLink(fileId: string, linkData: AddLink): Promise<StorageLink> {
        const response: AxiosResponse<StorageLink> = await this.api.post(
            `/api/v1/storage/${fileId}/links`,
            linkData
        );
        return response.data;
    }

    async deleteLink(linkId: string): Promise<{ success: boolean }> {
        const response: AxiosResponse<{ success: boolean }> = await this.api.delete(`/api/v1/storage/links/${linkId}`);
        return response.data;
    }

    async downloadLink(linkId: string): Promise<Blob> {
        const response = await this.api.get(`/api/v1/storage/link/download/${linkId}`, {
            responseType: 'blob'
        });
        return response.data;
    }

    async getSenders(): Promise<SenderDTO[]> {
        const response: AxiosResponse<SenderDTO[]> = await this.api.get('/api/v1/newsletter/senders');
        return response.data;
    }

    async addSender(request: AddSenderRequest): Promise<void> {
        await this.api.post('/api/v1/newsletter/senders', request);
    }

    async updateSender(id: number, request: UpdateSenderRequest): Promise<void> {
        await this.api.patch(`/api/v1/newsletter/senders/${id}`, request);
    }

    async deleteSender(id: number): Promise<void> {
        await this.api.delete(`/api/v1/newsletter/senders/${id}`);
    }

    async getNewsletters(page: number, size: number): Promise<ActionResponseResponsePageNewsletterWebDTO> {
        const response: AxiosResponse<ActionResponseResponsePageNewsletterWebDTO> = await this.api.get(
            '/api/v1/newsletter',
            {params: {page, size}}
        );
        return response.data;
    }

    async stopNewsletter(newsletterId: string): Promise<void> {
        await this.api.post(`/api/v1/newsletter/${newsletterId}/stop`);
    }

    async continueNewsletter(newsletterId: string): Promise<void> {
        await this.api.post(`/api/v1/newsletter/${newsletterId}/continue`);
    }

    async deleteNewsletter(newsletterId: string): Promise<{ success: boolean; msg?: string; body?: any }> {
        const response: AxiosResponse<{ success: boolean; msg?: string; body?: any }> = await this.api.delete(
            `/api/v1/newsletter/${newsletterId}`
        );
        return response.data;
    }

    async createNewsletter(request: CreateNewsletterRequest): Promise<{ success: boolean; msg?: string; body?: any }> {
        const formData = new FormData();
        formData.append('html', request.htmlFile);
        formData.append('target', request.recipientsFile);
        formData.append('senders', request.senders);

        let url = `/api/v1/newsletter?topic=${encodeURIComponent(request.topic)}`;
        if (request.scheduledDate) {
            url += `&scheduled_date=${encodeURIComponent(request.scheduledDate)}`;
        }

        const response: AxiosResponse<{ success: boolean; msg?: string; body?: any }> = await this.api.post(
            url,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    }

    async testNewsletter(request: TestNewsletterRequest): Promise<{ success: boolean; msg?: string }> {
        const formData = new FormData();
        formData.append('email', request.testEmail);
        formData.append('html', request.htmlFile);
        formData.append('target', request.recipientsFile);
        formData.append('senders', request.senders);

        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            `/api/v1/newsletter/test?topic=${encodeURIComponent(request.topic)}`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    }

    async launchNewsletter(request: LaunchNewsletterRequest): Promise<{ success: boolean; msg?: string }> {
        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            `/api/v1/newsletter/${request.newsletterId}/run`
        );
        return response.data;
    }

    async testExistingNewsletter(request: TestExistingNewsletterRequest): Promise<{ success: boolean; msg?: string }> {
        if (!request.testEmail || request.testEmail.trim() === '') {
            throw new Error('Email is required');
        }
        
        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            `/api/v1/newsletter/${request.newsletterId}/test`,
            null,
            {
                params: {
                    email: request.testEmail.trim()
                },
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
        return response.data;
    }

    async createWaNewsletter(request: CreateWaNewsletterRequest): Promise<{ success: boolean; msg?: string; body?: any }> {
        const formData = new FormData();
        formData.append('description', request.description);
        formData.append('template_id', request.templateId);
        formData.append('template_lang', request.templateLang);
        if (request.imageUrl) {
            formData.append('image_url', request.imageUrl);
        }
        formData.append('target', request.participantsFile);
        if (request.scheduledDate) {
            formData.append('scheduled_date', request.scheduledDate);
        }
        if (request.testPhones) {
            formData.append('test_phones', request.testPhones);
        }

        const response: AxiosResponse<{ success: boolean; msg?: string; body?: any }> = await this.api.post(
            '/api/v1/newsletter/wa_file',
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    }

    // WA Chat methods
    async getWAChats(page: number = 1, size: number = 30, q?: string): Promise<ResponsePageWAChatDTO> {
        const params: Record<string, string | number> = { page, size };
        if (q) {
            params.q = q;
        }
        const response: AxiosResponse<ResponsePageWAChatDTO> = await this.api.get('/api/v1/wa/chats', {
            params,
        });
        return response.data;
    }

    async getWAChatMessages(chatId: string): Promise<WAMessageDTO[]> {
        const response: AxiosResponse<WAMessageDTO[]> = await this.api.get(`/api/v1/wa/chats/${chatId}/messages`);
        return response.data;
    }

    async sendWAMessage(chatId: string, request: SendWAMessageRequest): Promise<{ success: boolean; msg?: string }> {
        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            `/api/v1/wa/chats/${chatId}/messages`,
            request
        );
        return response.data;
    }

    // WA Note methods
    async getWANote(phone: string): Promise<WANoteDTO> {
        const response: AxiosResponse<WANoteDTO> = await this.api.get(`/api/v1/wa/chats/${phone}/note`);
        return response.data;
    }

    async updateWANote(phone: string, request: UpdateWANoteRequest): Promise<{ success: boolean; msg?: string }> {
        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            `/api/v1/wa/chats/${phone}/note`,
            request
        );
        return response.data;
    }

    // SF Units methods
    async getSFProjects(): Promise<ProjectWebDTO[]> {
        const response: AxiosResponse<ProjectWebDTO[]> = await this.api.get('/api/v1/sf_units/projects');
        return response.data;
    }

    async getSFUnits(unitId?: string, projectId?: string): Promise<UnitWebDTO[]> {
        const params: any = {};
        if (unitId) params.unit_id = unitId;
        if (projectId) params.project_id = projectId;

        const response: AxiosResponse<UnitWebDTO[]> = await this.api.get('/api/v1/sf_units', { params });
        return response.data;
    }

    // Winter Offer methods
    async getWinterOfferMerchants(): Promise<OfferMerchant[]> {
        const response: AxiosResponse<OfferMerchant[]> = await this.api.get('/api/v1/winter_offer');
        return response.data;
    }

    async addWinterOfferMerchant(request: AddMerchantRequest): Promise<{ success: boolean; msg?: string; body?: any }> {
        const response: AxiosResponse<{ success: boolean; msg?: string; body?: any }> = await this.api.post(
            '/api/v1/winter_offer/merchants',
            request
        );
        return response.data;
    }

    async getWinterOfferMerchantDetail(merchantId: number): Promise<MerchantDetail> {
        const response: AxiosResponse<MerchantDetail> = await this.api.get(`/api/v1/winter_offer/merchants/${merchantId}`);
        return response.data;
    }

    async addWinterOfferSlots(merchantId: number, request: AddSlotsRequest): Promise<void> {
        await this.api.post(`/api/v1/winter_offer/${merchantId}/slots`, request);
    }

    // WA Templates methods
    async getWaTemplates(page: number, size: number, q?: string): Promise<ResponsePageWebTemplateDTO> {
        const params: any = { page, size };
        if (q) params.q = q;

        const response: AxiosResponse<ResponsePageWebTemplateDTO> = await this.api.get(
            '/api/v1/newsletter/wa/templates',
            { params }
        );
        return response.data;
    }

    async createWaTemplate(request: CreateTemplateRequest): Promise<{ success: boolean; msg?: string }> {
        const response: AxiosResponse<{ success: boolean; msg?: string }> = await this.api.post(
            '/api/v1/newsletter/wa/templates',
            request
        );
        return response.data;
    }

    async refreshWaTemplatesList(): Promise<void> {
        await this.api.get('/api/v1/newsletter/wa/templates/refresh_list');
    }
}

export const apiService = new ApiService();